import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, Observable, lastValueFrom, timer } from 'rxjs';
import { map, switchMap, takeWhile } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { Message, MessageService } from 'primeng/api';
import { ILogin, IRegister } from '../../model/auth.model';
import { ISibasiHTTPResponse } from '../../model/sibasiHTTPResponse.model';
import { TokenConstants } from 'src/assets/constants/token.const';
import { SIBASI_APPS } from '../../model/sibasi-system.model';
import { IAuthItem } from '../../model/user.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  TOKEN_KEY = TokenConstants.TOKEN_KEY;
  REFRESH_TOKEN = TokenConstants.REFRESH_TOKEN;
  redirectUrl = '';
  loginPageStatusMessage!: Message[];
  isRefreshing = false;

  // Services
  tokenDecoder = inject(JwtHelperService);

  private messageSubject = new BehaviorSubject<string | null>(null);
  message$ = this.messageSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  setAuthToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  navigateByUrl(url: string): void {
    this.router.navigateByUrl(url);
  }

  // handleLoginRedirect(): void {
  //     if (this.redirectUrl.includes('auth/login')) {
  //         this.router.navigateByUrl(this.redirectUrl);
  //     } else {
  //         this.router.navigateByUrl('/');
  //     }
  // }

  handleLoginRedirect(): void {
    if (
      this.redirectUrl.includes('pending-invites') ||
      this.redirectUrl.includes('profile')
    ) {
      this.router.navigateByUrl(this.redirectUrl);
    } else {
      if (environment.activeApp === SIBASI_APPS.EBMSUITE) {
        this.router.navigateByUrl('/app');
      } else {
        this.router.navigateByUrl('/');
      }
    }
  }

  login(
    userDetails: ILogin,
    invitedId?: string
  ): Observable<ISibasiHTTPResponse<{ access_token: string }>> {
    return this.httpClient
      .post<
        ISibasiHTTPResponse<{ access_token: string }>
      >(`${environment.api_url}user/loginUsingPassword`, userDetails)
      .pipe(
        map((res) => {
          const token = res.result.access_token;
          this.setAuthToken(token);

          const redirectURL = invitedId
            ? `/organization/pending-invites/${userDetails.authItemValue}/${invitedId}/true`
            : this.redirectUrl || '/';

          this.navigateByUrl(redirectURL);

          if (redirectURL.includes('auth/login')) {
            this.navigateByUrl('/');
          }

          return res;
        })
      );
  }

  sendGoogleDataToLogin(profileData: any): Observable<{ access_token: string }> {
    return this.httpClient
      .post<{
        access_token: string;
      }>(`${environment.api_url}google/auth/loginOrSignUp`, profileData)
      .pipe(
        map((res) => {
          const token = res.access_token;
          this.setAuthToken(token);
          const redirectUrl = this.redirectUrl || '/issue';
          this.navigateByUrl(redirectUrl);
          return res;
        })
      );
  }

  register(
    userDetails: IRegister,
    inviteId?: string
  ): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/signupWithEmail${inviteId ? '/' + inviteId : ''}`,
      userDetails
    );
    // .pipe(
    //     map((res) => {
    //         if (environment.activeApp === SIBASI_APPS.EBMSUITE) {
    //             const token = res.result.access_token;
    //             this.setAuthToken(token);
    //             this.handleLoginRedirect();
    //         }
    //         return res;
    //     })
    // );
  }
  registerWithPhone(userDetails: IRegister): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/generateOTP`,
      userDetails
    );
  }

  forgotPassword(email: any): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/forgotPassword`,
      email
    );
  }

  resetPassword(resetDetails: any): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/resetPassword`,
      resetDetails
    );
  }
  verifyAuthItem(
    verifyDetails: any
  ): Observable<ISibasiHTTPResponse<{ access_token: string }>> {
    return this.httpClient
      .post<
        ISibasiHTTPResponse<{ access_token: string }>
      >(`${environment.api_url}user/verifyAuthItem`, verifyDetails)
      .pipe(
        map((res) => {
          //   const token = res.result.access_token;
          //   this.setAuthToken(token);

          return res;
        })
      );
  }
  verifyOTP(
    verifyDetails: any
  ): Observable<ISibasiHTTPResponse<{ access_token: string }>> {
    return this.httpClient
      .post<
        ISibasiHTTPResponse<{ access_token: string }>
      >(`${environment.api_url}user/verifyOTPLogin`, verifyDetails)
      .pipe(
        map((res) => {
          const token = res.result.access_token;
          this.setAuthToken(token);
          this.handleLoginRedirect();
          return res;
        })
      );
  }
  generateOTP(authValue: any): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/generateOTP`,
      authValue
    );
  }
  getAuthToken(): string {
    const token = localStorage.getItem(this.TOKEN_KEY);
    return token as string;
  }

  isLoggedIn(): boolean {
    const token = this.getAuthToken();
    if (token) {
      const jwtExpirationTime = this.getJwtExpirationTime();
      const timeToRefresh = jwtExpirationTime - Date.now() - 10 * 1000;
      if (timeToRefresh > 0 && !this.isRefreshing) {
        this.isRefreshing = true;
        timer(timeToRefresh)
          .pipe(
            takeWhile(() => this.isRefreshing),
            switchMap(() => this.refreshToken())
          )
          .subscribe(() => {
            this.isRefreshing = false;
            this.isLoggedIn();
          });
      }
      return true;
    }
    return false;
  }
  getJwtExpirationTime(): number {
    const decodedToken = this.decodedToken();
    const expirationTime = decodedToken.exp * 1000;
    return expirationTime;
  }

  refreshToken(): Observable<any> {
    return this.httpClient
      .get<
        ISibasiHTTPResponse<{ refresh_token: string }>
      >(`${environment.api_url}user/refreshToken`)
      .pipe(
        switchMap((res) => {
          const refresh_token = res.result.refresh_token;

          localStorage.setItem('access_token', refresh_token);
          return refresh_token;
        })
      );
  }
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN);

    this.redirectUrl = '/auth/login';
    this.navigateByUrl(this.redirectUrl);
  }

  decodedToken(): any {
    const token = this.getAuthToken();
    return token ? this.tokenDecoder.decodeToken(token) : {};
  }

  getLoggedInUserId(): string {
    const decodedToken = this.decodedToken();
    const userId = decodedToken._id ? decodedToken._id : decodedToken.id;

    return userId;
  }
  getLoggedInUserName(): string {
    const decodedToken = this.decodedToken();
    const userName = decodedToken.fullname;
    return userName;
  }
  getLoggedInUserRferralCode(): string {
    const decodedToken = this.decodedToken();
    const referralCode = decodedToken.referralCode;
    return referralCode;
  }
  getLoggedInUserEmail(): string {
    const decodedToken = this.decodedToken();
    const authItems = decodedToken.authItem;
    let email = '';
    for (let i = 0; i < authItems.length; i++) {
      if (authItems[i].authType === 'EMAIL' && authItems[i].isVerified === true) {
        email = authItems[i].value;
      }
    }
    return email;
  }
  doesHaveRole(userRole: string[]) {
    const decodedToken = this.decodedToken();
    const userRoles = decodedToken.roles;
    if (userRoles?.some((r: any) => userRole.includes(r))) {
      return true;
    }
    return false;
  }

  getAuthTokenFromQueryParam(): string {
    let authToken = '';
    this.activatedRoute.queryParams.subscribe({
      next: (queryParams) => {
        authToken = queryParams['siba_si'];
      }
    });
    return authToken;
  }

  public verifyAuthTokenFromQueryParams(): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const res$ = this.httpClient.get<ISibasiHTTPResponse<{ access_token: string }>>(
          `${environment.api_url}verify/token`
        );
        const res = await lastValueFrom(res$);
        if (res?.result) {
          resolve(true);
        } else {
          resolve(false);
        }
      } catch (err) {
        resolve(false);
      }
    });
  }

  getUserEmail(): string {
    const decodedToken = this.decodedToken();
    const authItem = decodedToken.authItem[0].value;
    if (authItem) {
      return authItem;
    }

    return '';
  }

  setRedirectUrl(url: string): void {
    this.redirectUrl = url;
  }

  checkIfUserHasAVerifiedAuthItem(): boolean {
    const decodedToken = this.decodedToken();
    const hasVerifiedAuthItem = decodedToken.hasVerifiedAuthItem || false;
    return hasVerifiedAuthItem;
  }

  showError(message: string) {
    this.messageSubject.next(message);
  }
  resendVerificationEmail(): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.get<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/account/verification/email/resend`
    );
  }

  unLinkAuthItem(authItem: IAuthItem): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/removeAuthItem`,
      authItem
    );
  }

  linkAuthItem(authItem: IAuthItem): Observable<ISibasiHTTPResponse<any>> {
    return this.httpClient.post<ISibasiHTTPResponse<any>>(
      `${environment.api_url}user/addAuthItem`,
      authItem
    );
  }
}
